import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReservationRow } from '@smile/shared';
import { ApiError, api, type WaMessage, type WaNotice } from '../api/client';

/**
 * The booking page's WhatsApp card (docs/WHATSAPP.md): whether the customer
 * agreed to WhatsApp notifications (the new website's checkbox, or the desk
 * after a phone call — "Zákazník souhlasí"), the three notifications and
 * what Meta says happened to them, and the conversation. When the customer
 * has written within the last 24 hours the desk answers here; after that
 * WhatsApp allows no free reply and the card says to call.
 */
const KIND_LABEL: Record<string, string> = {
  confirmation: 'Potvrzení rezervace',
  flight: 'Dotaz na číslo zpátečního letu',
  arrival: 'Připomínka příjezdu (kód k boxu)',
  departure: 'Připomínka odjezdu (kód k vyzvednutí)',
};
const NOTICE_LABEL: Record<WaNotice['state'], string> = {
  claimed: 'odesílá se',
  sent: 'odesláno',
  logged: 'jen zapsáno (režim bez odesílání)',
  skipped: 'vynecháno — příjezd už byl blízko, šla rovnou připomínka',
  failed: 'neodešlo',
};
const STATUS_LABEL: Record<string, string> = {
  sent: '✓ odesláno',
  delivered: '✓✓ doručeno',
  read: '✓✓ přečteno',
  failed: '✗ neodešlo',
  logged: '· jen zapsáno',
  received: '',
};
const MODE_LABEL: Record<string, string> = { off: 'vypnuto', log: 'jen zápis, nic se neodesílá', send: 'odesílá se' };

function when(iso: string): string {
  return new Intl.DateTimeFormat('cs-CZ', { timeZone: 'Europe/Prague', day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
}

function errText(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : String(e);
}

export function WhatsAppCard({ r }: { r: ReservationRow }) {
  const qc = useQueryClient();
  const key = ['whatsapp', r.id];
  // Every refresh asks the hosting for the booking's number: only while WhatsApp is on.
  const q = useQuery({ queryKey: key, queryFn: () => api.whatsapp(r.id), refetchInterval: (query) => (query.state.data && query.state.data.enabled && query.state.data.mode !== 'off' ? 30_000 : false) });
  const [text, setText] = useState('');
  const consent = useMutation({
    mutationFn: (granted: boolean) => api.setWhatsAppConsent(r.id, granted),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
  const send = useMutation({
    mutationFn: () => api.whatsAppReply(r.id, text.trim()),
    onSuccess: () => {
      setText('');
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: ['whatsapp-inbox'] });
    },
  });

  if (q.isPending) return <section className="card detailcard"><h2>WhatsApp</h2><div className="empty">Načítám…</div></section>;
  if (q.isError) return <section className="card detailcard"><h2>WhatsApp</h2><div className="empty">Nelze načíst: {errText(q.error)}</div></section>;
  const d = q.data;
  if (!d.enabled || d.mode === 'off') {
    return (
      <section className="card detailcard">
        <h2>WhatsApp</h2>
        <div className="empty">WhatsApp zatím není zapnuté.</div>
      </section>
    );
  }
  const live = ['new', 'pending', 'paid', 'verified'].includes(r.status);
  const agreed = d.consent?.granted === true && !d.phoneChanged;
  const noticeOf = (k: string) => d.notices.find((n) => n.kind === k) ?? null;

  return (
    <section className="card detailcard wacard">
      <h2>WhatsApp</h2>
      <div className="wa-head">
        <span>{d.phone ?? 'bez použitelného čísla'}</span>
        <span className="wa-mode">· {MODE_LABEL[d.mode] ?? d.mode}</span>
      </div>

      <div className="wa-consent">
        {agreed ? (
          <>
            <span className="wa-ok">Zákazník souhlasí</span>{' '}
            <span className="wa-muted">({d.consent!.source === 'web' ? 'na webu' : d.consent!.source === 'web-notice' ? 'upozornění v rezervačním formuláři' : d.consent!.source === 'desk-auto' ? 'automaticky, rezervace z dispečinku' : 'telefonicky, dispečink'}, {when(d.consent!.at)})</span>
            {d.canWrite && live && (
              <button type="button" className="btn sm" disabled={consent.isPending} onClick={() => consent.mutate(false)}>
                Odvolat
              </button>
            )}
          </>
        ) : (
          <>
            <span className="wa-muted">
              {d.phoneChanged
                ? `Číslo v rezervaci se změnilo (souhlas byl pro +${d.consent!.phone}) — zprávy se neposílají, dokud zákazník neodsouhlasí nové číslo.`
                : d.consent?.source === 'stop-reply'
                  ? `Zákazník odpověděl STOP (${when(d.consent.at)}) — zprávy se neposílají.`
                  : 'Bez souhlasu — zprávy se neposílají.'}
            </span>
            {d.canWrite && live && d.phone && (
              <button
                type="button"
                className="btn sm primary"
                disabled={consent.isPending}
                title="Jen když zákazník do telefonu řekl, že chce potvrzení a připomínky na WhatsApp"
                onClick={() => consent.mutate(true)}
              >
                Zákazník souhlasí (telefonicky)
              </button>
            )}
          </>
        )}
        {consent.isError && <div className="arrive-error">{errText(consent.error)}</div>}
      </div>

      {agreed && (
        <ul className="wa-notices">
          {(['confirmation', 'arrival', 'departure', 'flight'] as const).map((k) => {
            const n = noticeOf(k);
            // The flight question goes out only when a booking has no flight number: shown once it exists.
            if (k === 'flight' && !n) return null;
            return (
              <li key={k}>
                <span>{KIND_LABEL[k]}</span>
                <span className="wa-muted">
                  {n ? `${NOTICE_LABEL[n.state]} ${when(n.at)}${n.state === 'failed' && n.error ? ` — ${n.error.replace(/^retry:/, 'zkusí se znovu: ')}` : ''}` : 'čeká'}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {d.messages.length > 0 && (
        <div className="wa-thread">
          {d.messages
            .filter((m: WaMessage) => m.kind !== 'dismissed')
            .map((m: WaMessage) => (
              <div key={m.id} className={m.direction === 'in' ? 'wa-msg in' : 'wa-msg out'}>
                <div className="wa-body">{m.body}</div>
                <div className="wa-meta">
                  {when(m.at)} {m.direction === 'out' ? STATUS_LABEL[m.status] ?? m.status : 'zákazník'}
                  {m.error ? ` — ${m.error}` : ''}
                </div>
              </div>
            ))}
        </div>
      )}

      {d.windowUntil ? (
        d.canWrite && d.mode === 'send' ? (
          <form
            className="wa-reply"
            onSubmit={(e) => {
              e.preventDefault();
              if (text.trim()) send.mutate();
            }}
          >
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={4000} placeholder={`Odpověď zákazníkovi (lze do ${when(d.windowUntil)})`} />
            <button type="submit" className="btn sm primary" disabled={send.isPending || !text.trim()}>
              {send.isPending ? 'Odesílám…' : 'Odeslat na WhatsApp'}
            </button>
            {send.isError && <div className="arrive-error">{errText(send.error)}</div>}
          </form>
        ) : (
          <div className="wa-muted">Odpovídání je na tomto nasazení vypnuté.</div>
        )
      ) : (
        d.messages.some((m) => m.direction === 'in') && <div className="wa-muted">Zákazník nepsal posledních 24 hodin — WhatsApp nedovolí volnou odpověď. Zavolejte nebo napište e-mail.</div>
      )}
    </section>
  );
}
